import { StreamLanguage, type StringStream } from "@codemirror/language";
import type { Extension } from "@uiw/react-codemirror";
import { EDITOR_SURFACE } from "../../common/editor-theme";

// There is no @codemirror/lang-csv and a delimited file needs no grammar: what is worth telling apart is the
// character that ends a field, the quotes that let one hold that character, and a field that is a number. The mode
// is built per delimiter because the delimiter is the whole of what it knows.
function csvLanguage(delimiter: string) {
  return StreamLanguage.define<QuoteState>({
    name: "csv",
    startState: () => ({ inQuotes: false, atFieldStart: true }),

    token(stream, state) {
      // A quoted field carries newlines, so a line may open inside one the line above it started.
      if (state.inQuotes) return takeQuoted(stream, state);

      if (stream.sol()) state.atFieldStart = true;

      if (stream.eat(delimiter)) {
        state.atFieldStart = true;
        return "separator";
      }

      // Only where a field begins, as in the parser: a quote in the middle of one is a character somebody typed,
      // and reading it as an opening quote would paint the rest of the document as one long string.
      if (state.atFieldStart && stream.eat("\"")) {
        state.inQuotes = true;
        state.atFieldStart = false;
        return takeQuoted(stream, state);
      }

      // A bare field runs to the next delimiter or to the end of the line, whichever comes first.
      state.atFieldStart = false;
      while (!stream.eol() && stream.peek() !== delimiter) stream.next();
      if (stream.current() === "") stream.next();

      return NUMBER.test(stream.current()) ? "number" : null;
    },
  });
}

interface QuoteState {
  inQuotes: boolean;
  atFieldStart: boolean;
}

function takeQuoted(stream: StringStream, state: QuoteState): string {
  while (!stream.eol()) {
    if (stream.next() !== "\"") continue;
    // A doubled quote is one quote of the value, so the second is eaten rather than left to close the field.
    if (stream.peek() === "\"") {
      stream.next();
      continue;
    }
    state.inQuotes = false;
    break;
  }
  return "string";
}

// Held rather than rebuilt: a new array reconfigures the running editor, and a delimiter switched back and forth
// would otherwise be two reconfigurations of an editor that ended up exactly as it started.
const HELD = new Map<string, Extension[]>();

export function editorExtensions(delimiter: string): Extension[] {
  const held = HELD.get(delimiter);
  if (held) return held;

  // No line wrapping, unlike the prose editors: a record is a line here, so the line numbers count records and a
  // wrapped one would put the same row at two heights. What a long row is read in is the table below it.
  const built = [csvLanguage(delimiter), ...EDITOR_SURFACE];
  HELD.set(delimiter, built);
  return built;
}

// Anchored, since a field is a number only when the whole of it is one.
const NUMBER = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/;
